import React, { useEffect, useMemo, useState} from 'react'
import './AdminDashboardHome.css';
import { getAds } from '../../../services/getAds';
import ScreenActivityGuageChart from './ScreenActivityGaugeChart/ScreenActivityGuageChart';
import { getActiveScreens } from '../../../services/getActiveScreens';
import { sendScreenSummaryReport } from '../../../services/sendScreenSummaryReport';
import { restartPluginWorker } from '../../../services/restartPluginWorker';
import { useSelector } from 'react-redux'
import { getToken } from '../../../features/token/tokenSaver';

function getPacificTodayLabel() {
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date());
  } catch {
    return 'Today (Pacific)';
  }
}

export default function AdminDashboardHome(props) {
  const [adsShownToday, setAdsShownToday] = useState(0);
  const [adsShown, setAdsShown] = useState(0);
  const [screensActive, setScreensActive] = useState(0);
  const [totalScreens, setTotalScreens] = useState(0);
  const [summarySending, setSummarySending] = useState(false);
  const [summaryMessage, setSummaryMessage] = useState('');
  const [workerRestarting, setWorkerRestarting] = useState(false);
  const [workerMessage, setWorkerMessage] = useState('');
  const [workerMessageType, setWorkerMessageType] = useState('');
  const token = useSelector(getToken)
  const pacificTodayLabel = useMemo(() => getPacificTodayLabel(), []);

  async function handleSendScreenSummary() {
    if (!token || summarySending) return;
    setSummarySending(true);
    setSummaryMessage('');
    try {
      const response = await sendScreenSummaryReport(token);
      const data = response.data || {};
      if (data.success) {
        setSummaryMessage(
          `Screen summary sent to ${data.recipient}. ${data.slideCount ?? 0} slides across ${data.screenCount ?? 0} screens.`
        );
      } else {
        setSummaryMessage(data.error || 'Could not send screen summary.');
      }
    } catch (err) {
      setSummaryMessage(
        err?.response?.data?.error || 'Could not send screen summary. Check that email is configured on the server.'
      );
    } finally {
      setSummarySending(false);
    }
  }

  async function handleRestartPluginWorker() {
    if (!token || workerRestarting) return;
    const confirmed = window.confirm(
      'Restart the plugin worker? It will pull the latest code from GitHub and be briefly unavailable while it reboots.'
    );
    if (!confirmed) return;
    setWorkerRestarting(true);
    setWorkerMessage('');
    setWorkerMessageType('');
    try {
      const response = await restartPluginWorker(token);
      const data = response.data || {};
      if (data.success) {
        setWorkerMessageType('success');
        setWorkerMessage(data.message || 'Plugin worker is restarting and will pull the latest code from GitHub.');
      } else {
        setWorkerMessageType('error');
        setWorkerMessage(data.error || 'Could not restart the plugin worker.');
      }
    } catch (err) {
      setWorkerMessageType('error');
      setWorkerMessage(
        err?.response?.data?.error || 'Could not restart the plugin worker. Check that it is reachable from the API.'
      );
    } finally {
      setWorkerRestarting(false);
    }
  }

  useEffect(() => {
    getAds(token).then(response => {
      setAdsShown(response.data.adsShown || 0);
      setAdsShownToday(response.data.adsShownToday || 0);
    }).catch(e => {
      
    });
    getActiveScreens(token).then(response => {
      setScreensActive(response.data.activeScreens || 0);
      setTotalScreens(response.data.totalScreens || 0);
    }).catch(e => {

    });
    return () => {
      
    }
  }, [])

  const activityPercent = useMemo(() => {
    if (!totalScreens) {
      return '0%';
    }
    return `${Math.round((screensActive / totalScreens) * 100)}%`;
  }, [screensActive, totalScreens]);
  
  return (

    <div className='adminDashboardContainer'>
      <div className='adminDashboardHomeContainer'>
        <div className='adminDashboardHeader'>
          <h1 className='adminDashboardHometitle'>Admin Overview</h1>
          <span className='adminDashboardSubTitle'>Platform health and delivery activity at a glance</span>
        </div>

        <div className='adminDashboardStatsGrid'>
          <div className='adminKpiCard primary'>
            <span className='adminKpiLabel'>Ads Displayed Today</span>
            <span className='adminKpiValue'>{adsShownToday}</span>
          </div>

          <div className='adminKpiCard'>
            <span className='adminKpiLabel'>Total Ads Displayed</span>
            <span className='adminKpiValue dark'>{adsShown}</span>
          </div>

          <div className='adminKpiCard'>
            <span className='adminKpiLabel'>Active Screens</span>
            <span className='adminKpiValue dark'>{screensActive}</span>
          </div>

          <div className='adminKpiCard primary'>
            <span className='adminKpiLabel'>Screen Activity</span>
            <span className='adminKpiValue'>{activityPercent}</span>
          </div>
        </div>

        <div className='adminDashboardHomeGraph'> 
          <ScreenActivityGuageChart screensActive={screensActive} totalScreens={totalScreens}></ScreenActivityGuageChart>
          <div className='adminDashboardGraphFooter'>
            <span className='adminDashboardGraphFooterText'>Active {screensActive} of {totalScreens} registered screens</span>
          </div>
        </div>

        <div className='adminDashboardHomeStatsContainer'>
          <div className='adminTotalScreensCard'>
            <span className='adminKpiLabel'>Total Screens</span>
            <span className='adminKpiValue'>{totalScreens}</span>
          </div>
        </div>

        <div className='adminReportsCard'>
          <div className='adminReportsCardHeader'>
            <h2 className='adminReportsTitle'>Reports</h2>
            <span className='adminReportsSubtitle'>Daily screen summary · {pacificTodayLabel} (Pacific)</span>
          </div>
          <p className='adminReportsDescription'>
            Sends a hierarchical report of screen time, slides played, and top ads for today. Scheduled automatically every day at 5:00 PM Pacific.
          </p>
          <button
            type='button'
            className='adminReportsSendButton'
            onClick={handleSendScreenSummary}
            disabled={summarySending || !token}
          >
            {summarySending ? 'Sending…' : 'Send screen summary to me'}
          </button>
          {summaryMessage ? (
            <p className={`adminReportsStatus ${summaryMessage.includes('sent to') ? 'success' : 'error'}`}>
              {summaryMessage}
            </p>
          ) : null}
        </div>

        <div className='adminReportsCard'>
          <div className='adminReportsCardHeader'>
            <h2 className='adminReportsTitle'>Plugin Worker</h2>
            <span className='adminReportsSubtitle'>Background render &amp; social refresh service</span>
          </div>
          <p className='adminReportsDescription'>
            Restarts the plugin worker process. On boot it pulls the latest code from GitHub, so use this to deploy worker updates. The worker will be briefly unavailable while it reboots.
          </p>
          <button
            type='button'
            className='adminReportsSendButton'
            onClick={handleRestartPluginWorker}
            disabled={workerRestarting || !token}
          >
            {workerRestarting ? 'Restarting…' : 'Restart plugin worker'}
          </button>
          {workerMessage ? (
            <p className={`adminReportsStatus ${workerMessageType}`}>
              {workerMessage}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  )
}
